import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';

import Button from '../shared/Button';
import ProgramRegistrationModal from './ProgramRegistrationModal';
import ProgramAdminFormModal, {
  defaultProgramForm,
  formToProgramPayload,
  programToForm,
} from './ProgramAdminFormModal';
import ConfirmActionModal from '../shared/ConfirmActionModal';
import axiosInstance from '../../api/axiosInstance';
import { programsAPI } from '../../api/programs';
import { useAuth } from '../../contexts/AuthContext';
import { trackPageView, trackButtonClick } from '../../utils/analytics';

const ProgramPage = () => {
  const { slug, sport } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [defaultCoach, setDefaultCoach] = useState(null);

  const { isAdmin } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [programForm, setProgramForm] = useState(defaultProgramForm);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    description: '',
    actionType: null,
  });

  useEffect(() => {
    if (program) {
      trackPageView(`Program: ${program.title}`);
    }
  }, [program]);

  const loadProgram = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (slug && !Number.isNaN(Number(slug))) {
        response = await axiosInstance.get(`/trainings/${slug}/`);
        if (response.data) {
          setProgram(response.data);
          return;
        }
      }

      response = await axiosInstance.get(`/trainings/?slug=${slug}`);
      const programs = response.data;
      if (programs && programs.length > 0) {
        setProgram(programs[0]);
      } else {
        setError('Программа не найдена');
      }
    } catch (err) {
      console.error('Error fetching program:', err);
      setError('Ошибка загрузки программы');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) {
      loadProgram();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    const fetchDefaultCoach = async () => {
      try {
        const response = await axiosInstance.get('/team-members/?first_name=Виталий&last_name=Лейкин&is_active=true');
        if (response.data && response.data.length > 0) {
          setDefaultCoach(response.data[0]);
        }
      } catch (err) {
        console.error('Error fetching default coach:', err);
      }
    };
    fetchDefaultCoach();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchCoaches = async () => {
      try {
        const response = await axiosInstance.get('/team-members/?is_active=true');
        const list = (response.data || []).filter((item) => ['head_coach', 'coach', 'nutrition'].includes(item.role));
        setCoaches(list);
      } catch (err) {
        console.error('Error fetching coaches:', err);
      }
    };
    fetchCoaches();
  }, [isAdmin]);

  const handleRegisterClick = () => {
    trackButtonClick('Register Program');
    setShowRegistration(true);
  };

  const toCoachSlug = (coachValue) => {
    const map = {
      а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y',
      к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f',
      х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
    };
    return String(coachValue || '')
      .toLowerCase()
      .split('')
      .map((ch) => map[ch] ?? ch)
      .join('')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const openCoachProfile = (coachValue) => {
    if (!coachValue) return;
    const coachSlug = toCoachSlug(`${coachValue.first_name}-${coachValue.last_name}`);
    const params = new URLSearchParams({
      coach: coachSlug,
      back: location.pathname,
    });
    navigate(`/coaches?${params.toString()}`);
  };

  const openEditModal = () => {
    if (!program) return;
    setProgramForm(programToForm(program));
    setShowFormModal(true);
  };

  const handleUpdateProgram = async () => {
    if (!program?.id) return;
    if (!programForm.title) {
      window.alert('Название программы обязательно.');
      return;
    }

    try {
      setIsSubmitting(true);
      await programsAPI.updateProgram(program.id, formToProgramPayload(programForm));
      setShowFormModal(false);
      await loadProgram();
    } catch (err) {
      console.error('Error updating program:', err);
      window.alert('Не удалось сохранить изменения программы.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProgram = async () => {
    if (!program?.id) return;
    try {
      await programsAPI.deleteProgram(program.id);
      navigate('/programs');
    } catch (err) {
      console.error('Error deleting program:', err);
      window.alert('Не удалось удалить программу.');
    }
  };

  const openConfirm = ({ title, description, actionType }) => {
    setConfirmModal({ isOpen: true, title, description, actionType });
  };

  const closeConfirm = () => {
    setConfirmModal({ isOpen: false, title: '', description: '', actionType: null });
  };

  const confirmAction = async () => {
    const { actionType } = confirmModal;
    closeConfirm();
    if (actionType === 'save') {
      await handleUpdateProgram();
      return;
    }
    if (actionType === 'delete') {
      await handleDeleteProgram();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center pt-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#662d91]"></div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center pt-24">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Программа не найдена</h2>
          <Button onClick={() => navigate('/')} withArrow>
            Вернуться на главную
          </Button>
        </div>
      </div>
    );
  }

  const features = [
    { text: program.feature1, color: program.feature1_color },
    { text: program.feature2, color: program.feature2_color },
    { text: program.feature3, color: program.feature3_color },
    { text: program.feature4, color: program.feature4_color },
  ].filter((f) => f.text);

  const benefits = [program.benefit1, program.benefit2, program.benefit3, program.benefit4].filter(Boolean);
  const coach = program.coach || defaultCoach;
  const landingSport = sport || program.sport_type || '';

  return (
    <div className="pt-32 lg:pt-36 bg-white text-gray-900 min-h-screen">
      <section className="bg-white text-gray-900 py-20 lg:py-24">
        <div className="max-w-[1040px] mx-auto px-5 sm:px-7 lg:px-8 space-y-12">
          {isAdmin && (
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={openEditModal}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100"
              >
                <Pencil className="w-4 h-4" />
                Редактировать
              </button>
              <button
                type="button"
                onClick={() => openConfirm({
                  title: 'Подтвердите удаление',
                  description: 'Удалить эту программу?',
                  actionType: 'delete',
                })}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100"
              >
                <Trash2 className="w-4 h-4" />
                Удалить
              </button>
            </div>
          )}

          <div className="space-y-8 max-w-[760px] mx-auto">
            {sport && (
              <div className="inline-flex items-center rounded-full bg-[#4897d2]/10 text-[#2d6e9f] px-4 py-1.5 text-xs font-semibold uppercase tracking-wide">
                Спец-страница для рекламы: {sport}
              </div>
            )}
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-center md:text-left">
                  {program.about_program_title || 'О программе'}
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed mb-6">
                  {program.about_program || 'Профессиональная программа тренировок'}
                </p>
                {benefits.length > 0 && (
                  <div className="grid sm:grid-cols-2 gap-4 auto-rows-fr">
                    {benefits.map((point, index) => (
                      <div key={index} className="rounded-2xl border border-gray-200 px-5 py-4 shadow-sm bg-white h-full flex items-center">
                        <p className="text-sm text-gray-600 leading-relaxed">• {point}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {features.length > 0 && (
                <div>
                  <h3 className="text-3xl font-bold mb-5 text-center md:text-left">
                    {program.features_title || 'Особенности программы'}
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4 auto-rows-fr">
                    {features.map((feature, index) => (
                      <div key={index} className="rounded-2xl border border-gray-200 px-5 py-4 shadow-sm bg-white h-full flex items-center">
                        <p className="text-sm text-gray-600 leading-relaxed">
                          <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: feature.color }} />
                          {feature.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="max-w-[760px] mx-auto rounded-2xl border border-gray-200 bg-gray-50 p-7 lg:p-8 shadow-lg space-y-5">
              <h3 className="text-3xl font-semibold text-gray-900">{program.details_section_title || 'Формат участия'}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2rem] text-gray-500">Тренер</p>
                  {coach ? (
                    <button
                      type="button"
                      onClick={() => openCoachProfile(coach)}
                      className="text-xl font-semibold text-gray-900 mt-1 text-left hover:text-[#4897d2] transition-colors"
                    >
                      {coach.first_name} {coach.last_name}
                    </button>
                  ) : (
                    <p className="text-xl font-semibold text-gray-900 mt-1">Назначается после консультации</p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2rem] text-gray-500">{program.details_duration_label || 'Длительность'}</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">{program.details_duration || 'Помесячная программа'}</p>
                  <p className="text-sm text-gray-600">{program.details_duration_hint || 'возможность продления'}</p>
                </div>
              </div>
              {program.details_location && (
                <div>
                  <p className="text-xs uppercase tracking-[0.2rem] text-gray-500">{program.details_location_label || 'Локация'}</p>
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">{program.details_location}</p>
                </div>
              )}
              <div className="pt-2">
                <Button onClick={handleRegisterClick} withArrow className="w-full sm:w-auto">
                  {program.registration_button_text || 'Оставить заявку'}
                </Button>
                <p className="text-xs text-gray-500 mt-2">{program.registration_footer_text || 'Мы свяжемся с вами в ближайшее время.'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showRegistration && (
        <ProgramRegistrationModal
          program={program}
          landingSport={landingSport}
          onClose={() => setShowRegistration(false)}
        />
      )}

      <ProgramAdminFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSubmit={(e) => {
          e.preventDefault();
          openConfirm({
            title: 'Подтвердите изменение',
            description: 'Сохранить изменения программы?',
            actionType: 'save',
          });
        }}
        isSubmitting={isSubmitting}
        editingProgramId={program.id}
        programForm={programForm}
        setProgramForm={setProgramForm}
        coaches={coaches}
      />

      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        description={confirmModal.description}
        onConfirm={confirmAction}
        onCancel={closeConfirm}
        loading={isSubmitting}
      />
    </div>
  );
};

export default ProgramPage;
